import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  Copy,
  Clock3,
  Download,
  FileCheck2,
  FileText,
  LockKeyhole,
  Mail,
  Megaphone,
  Pencil,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import Button from '../common/design-system/Button';
import Avatar from '../common/Avatar';
import ImageWithFallback from '../common/ImageWithFallback';
import { showToast } from '../common/toastService';
import { useAuth } from '../auth/AuthContext';
import PaidCollabPaymentStatus from '../payments/PaidCollabPaymentStatus';
import {
  completeCollabApplication,
  confirmStripeCheckoutSession,
  duplicateCollabOffer,
  downloadCollabReport,
  getCollabApplications,
  getDirectInvitations,
  getCollabPaymentStatus,
  getCollabSummary,
  getRecipientInvitations,
  publishCollabOffer,
  sendRecipientInvites,
} from '../../services/api';
import api from '../../services/api';
import i18n from '../../i18n';
import styles from './BusinessCollabJourney.module.scss';

const TERMINAL_PAYMENT_STATES = new Set(['paid', 'partially_refunded', 'fully_refunded', 'disputed']);
const journeyT = (key, options = {}) => i18n.t(`businessJourney.${key}`, { ...options, ns: 'collab' });

const PAYMENT_STATUS_KEYS = {
  unfunded: 'unfunded',
  checkout_pending: 'checkout_pending',
  processing: 'processing',
  paid: 'paid',
  partially_refunded: 'partially_refunded',
  fully_refunded: 'fully_refunded',
  disputed: 'disputed',
};

const JOURNEY_STEPS = new Set(['review', 'funding', 'publish', 'creators', 'deliverables', 'payments', 'completed']);

const formatDate = (value) => value
  ? new Intl.DateTimeFormat((i18n.resolvedLanguage || i18n.language || 'es').startsWith('pt') ? 'pt-BR' : (i18n.resolvedLanguage || i18n.language || 'es').startsWith('en') ? 'en-US' : 'es-PA', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(value))
  : journeyT('common.empty');

const formatMoney = (value, currency = 'USD') => new Intl.NumberFormat((i18n.resolvedLanguage || i18n.language || 'es').startsWith('pt') ? 'pt-BR' : (i18n.resolvedLanguage || i18n.language || 'es').startsWith('en') ? 'en-US' : 'es-PA', {
  style: 'currency', currency, currencyDisplay: 'code', minimumFractionDigits: 2,
}).format(Number(value || 0));

const stripMarkup = (value = '') => String(value).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const readError = (error, fallback) => error?.response?.data?.error || error?.response?.data?.detail || fallback;

const isPaidOffer = (offer) => ['paid', 'hybrid'].includes(offer?.compensation_mode) || Boolean(offer?.sponsor_this_offer);

const stepDefinitions = (paid, language) => {
  const translate = (key) => journeyT(key, { lng: language });
  return [
    { id: 'review', number: '01', label: translate('steps.review'), icon: FileText },
    ...(paid ? [{ id: 'funding', number: '02', label: translate('steps.funding'), icon: CircleDollarSign }] : []),
    ...(paid ? [{ id: 'publish', number: '03', label: translate('steps.publish'), icon: Megaphone }] : []),
    { id: 'creators', number: paid ? '04' : '02', label: translate('steps.creators'), icon: Users },
    { id: 'deliverables', number: paid ? '05' : '03', label: translate('steps.deliverables'), icon: FileCheck2 },
    ...(paid ? [{ id: 'payments', number: '06', label: translate('steps.payments'), icon: ShieldCheck }] : []),
    { id: 'completed', number: paid ? '07' : '04', label: translate('steps.completed'), icon: CheckCircle2 },
  ];
};

const getApplicationCreatorName = (application) => (
  application.creator_full_name || application.creator_username || journeyT('common.creator')
);

const getApplicationUsername = (application) => application.creator_username || '';

const getApplicationDeliverables = (application) => Array.isArray(application.deliverables) ? application.deliverables : [];

const DELIVERABLE_TYPE_ALIASES = {
  '1': 'post', post: 'post', posts: 'post', feed: 'post',
  '2': 'story', story: 'story', stories: 'story',
  '3': 'reel', reel: 'reel', reels: 'reel',
  '4': 'highlight', highlight: 'highlight', highlights: 'highlight',
};

const getDeliverableProgress = (offer, application) => {
  const submitted = getApplicationDeliverables(application);
  const required = Object.entries(offer?.deliverables_counts || {}).reduce((result, [rawType, rawCount]) => {
    const postType = DELIVERABLE_TYPE_ALIASES[String(rawType).trim().toLowerCase()];
    const count = Number(rawCount);
    if (postType && Number.isFinite(count) && count > 0) result[postType] = (result[postType] || 0) + count;
    return result;
  }, {});
  const submittedByType = submitted.reduce((result, item) => {
    const postType = DELIVERABLE_TYPE_ALIASES[String(item?.post_type || '').trim().toLowerCase()] || item?.post_type;
    if (postType) result[postType] = (result[postType] || 0) + 1;
    return result;
  }, {});
  const requiredTotal = offer?.deliverables_mode === 'defined'
    ? Object.values(required).reduce((total, count) => total + count, 0)
    : null;
  const isComplete = requiredTotal === null
    ? submitted.length > 0
    : requiredTotal > 0 && Object.entries(required).every(([postType, count]) => (submittedByType[postType] || 0) >= count);
  return { submittedTotal: submitted.length, requiredTotal, isComplete };
};

const getCoveredCreatorCount = (offer, applications = []) => Math.max(
  applications.filter((application) => ['accepted', 'completed'].includes(application.status)).length,
  Number(offer?.active_collabs_count || 0),
);

const getDeliverableInsights = (deliverable) => deliverable?.insights || deliverable?.insights_data || {};

const formatMetric = (value) => value === null || value === undefined || value === '' ? journeyT('common.empty') : Number(value).toLocaleString((i18n.resolvedLanguage || i18n.language || 'es').startsWith('pt') ? 'pt-BR' : (i18n.resolvedLanguage || i18n.language || 'es').startsWith('en') ? 'en-US' : 'es-PA');

const formatPercent = (value) => value === null || value === undefined || value === '' ? journeyT('common.empty') : `${Number(value).toFixed(1)}%`;

const PAYMENT_PROTECTED_STATES = new Set(['review_pending', 'refund_window_open', 'scheduled', 'blocked', 'processing']);

const getPaymentAllocationState = (allocation) => {
  if (allocation?.transfer_status === 'blocked') {
    if (allocation.transfer_block_reason === 'refund_request_open') return { label: journeyT('payments.status.refund_review'), tone: 'refund' };
    if (allocation.compliance_hold || allocation.transfer_block_reason === 'compliance_hold') return { label: journeyT('payments.status.held'), tone: 'refund' };
    return { label: journeyT('payments.status.in_review'), tone: 'refund' };
  }
  switch (allocation?.transfer_status) {
    case 'transferred': return { label: journeyT('payments.status.available'), tone: 'available' };
    case 'refund_window_open': return { label: journeyT('payments.status.processing'), tone: 'processing' };
    case 'scheduled': return { label: journeyT('payments.status.scheduled'), tone: 'processing' };
    case 'processing': return { label: journeyT('payments.status.processing_short'), tone: 'processing' };
    case 'review_pending': return { label: journeyT('payments.status.review_pending'), tone: 'processing' };
    case 'not_eligible': return { label: journeyT('payments.status.not_eligible'), tone: 'processing' };
    case 'failed': return { label: journeyT('payments.status.failed'), tone: 'refund' };
    case 'partially_reversed': return { label: journeyT('payments.status.partially_reversed'), tone: 'refund' };
    case 'fully_reversed': return { label: journeyT('payments.status.fully_reversed'), tone: 'refund' };
    default: return { label: journeyT('payments.status.pending'), tone: 'processing' };
  }
};

const getAvailabilityLabel = (allocation) => {
  if (allocation?.transfer_status === 'transferred') return allocation.transferred_at
    ? journeyT('payments.availability.available_since', { date: formatDate(allocation.transferred_at) })
    : journeyT('payments.availability.available');
  if (allocation?.payout_available_at) return journeyT('payments.availability.available_on', { date: formatDate(allocation.payout_available_at) });
  const dates = [];
  if (!allocation?.refund_window_closed_at && allocation?.refund_request_deadline) dates.push(allocation.refund_request_deadline);
  if (allocation?.transfer_release_at) dates.push(allocation.transfer_release_at);
  if (dates.length) return journeyT('payments.availability.available_on', { date: formatDate(dates.sort((first, second) => new Date(second) - new Date(first))[0]) });
  return journeyT('payments.availability.pending');
};

const getRefundWindowLabel = (allocation) => {
  if (allocation?.transfer_status === 'not_eligible' || allocation?.approval_status === 'awaiting_delivery') return journeyT('payments.refund.activate_on_approval');
  if (allocation?.refund_window_closed_at) return journeyT('payments.refund.closed', { date: formatDate(allocation.refund_window_closed_at) });
  if (allocation?.refund_request_deadline) return journeyT('payments.refund.until', { date: formatDate(allocation.refund_request_deadline) });
  return journeyT('payments.refund.opens_after_approval');
};

const getAllocationTimelineLabel = (allocation) => {
  const date = allocation?.approved_at || allocation?.delivery_submitted_at;
  if (!date) return journeyT('payments.timeline.pending_approval');
  if (allocation?.approved_at) return journeyT('payments.timeline.approved', { date: formatDate(date) });
  return journeyT('payments.timeline.delivered', { date: formatDate(date) });
};

const getDeliverableUrl = (deliverable) => deliverable?.post_url || deliverable?.instagram_media_url || '';

const getDeliverableType = (deliverable) => {
  const rawType = deliverable?.post_type || deliverable?.type || '';
  const alias = DELIVERABLE_TYPE_ALIASES[String(rawType).trim().toLowerCase()] || rawType;
  return {
    reel: journeyT('deliverables.types.reel'),
    story: journeyT('deliverables.types.story'),
    post: journeyT('deliverables.types.post'),
    highlight: journeyT('deliverables.types.highlight'),
  }[alias] || alias || journeyT('deliverables.content');
};

const JourneyHeader = ({ offer, currentStep, steps, onExit, onRefresh, refreshing }) => {
  const currentIndex = Math.max(0, steps.findIndex((step) => step.id === currentStep));
  const collabTitle = offer?.title || journeyT('common.untitled_collab');
  return (
    <header className={styles.topbar}>
      <div className={styles.topbarBrand}>
        <span className={styles.brandMark}>FOTH</span>
        <span className={styles.brandContext}>{journeyT('header.workspace')}</span>
        <div className={styles.collabIdentity} title={collabTitle}>
          <span>{journeyT('header.collab_label')}</span>
          <strong>{collabTitle}</strong>
        </div>
      </div>
      <div className={styles.topbarProgress} aria-label={journeyT('header.step_progress_aria', { current: currentIndex + 1, total: steps.length })}>
        <span>{journeyT('header.step_progress', { current: String(currentIndex + 1).padStart(2, '0'), total: String(steps.length).padStart(2, '0') })}</span>
        <div className={styles.progressTrack} aria-hidden="true">
          <span style={{ width: `${((currentIndex + 1) / steps.length) * 100}%` }} />
        </div>
        <strong>{steps[currentIndex]?.label}</strong>
      </div>
      <div className={styles.topbarActions}>
        <button className={styles.refreshButton} type="button" onClick={onRefresh} disabled={refreshing} aria-label={journeyT('header.refresh')}>
          <RefreshCw size={16} className={refreshing ? styles.spin : ''} />
        </button>
        <button className={styles.exitButton} type="button" onClick={onExit}>
          <ArrowLeft size={16} /> {journeyT('header.back')}
        </button>
      </div>
      <div className={styles.mobileTitle}>
        <span title={collabTitle}>{collabTitle}</span>
        <strong>{steps[currentIndex]?.number} · {steps[currentIndex]?.label}</strong>
      </div>
    </header>
  );
};

const JourneyRail = ({ steps, activeStep, completedSteps, availableSteps = new Set(), onSelect }) => (
  <aside className={styles.rail} aria-label={journeyT('rail.aria')}>
    <div className={styles.railEyebrow}>{journeyT('rail.eyebrow')}</div>
    <ol>
      {steps.map((step) => {
        const Icon = step.icon;
        const isCurrent = step.id === activeStep;
        const isDone = completedSteps.has(step.id);
        const isAvailable = isDone || availableSteps.has(step.id);
        return (
          <li key={step.id}>
            <button
              type="button"
              className={`${styles.railItem} ${isCurrent ? styles.current : ''} ${isDone ? styles.done : ''} ${!isDone && isAvailable ? styles.available : ''}`}
              onClick={() => onSelect(step.id)}
              disabled={!isCurrent && !isAvailable}
              aria-current={isCurrent ? 'step' : undefined}
            >
              <span className={styles.railNumber}>{isDone ? <Check size={14} /> : <Icon size={15} />}</span>
              <span><strong>{step.number} {step.label}</strong><small>{isDone ? journeyT('rail.completed') : isCurrent ? journeyT('rail.in_progress') : isAvailable ? journeyT('rail.available') : journeyT('rail.next')}</small></span>
            </button>
          </li>
        );
      })}
    </ol>
    <div className={styles.railNote}><LockKeyhole size={14} /> {journeyT('rail.controlled')}</div>
  </aside>
);

const StepHeader = ({ eyebrow, title, description, number }) => (
  <div className={styles.stepHeader}>
    <div>
      <span className={styles.eyebrow}>{eyebrow}</span>
      <h1>{title}</h1>
      {description && <p>{description}</p>}
    </div>
    <span className={styles.stepNumber}>{number}</span>
  </div>
);

const StatusPill = ({ tone = 'neutral', children }) => <span className={`${styles.statusPill} ${styles[`tone-${tone}`]}`}>{children}</span>;

const ReviewStep = ({ offer, goNext, onPublish, publishing, onEdit, onCandidates, steps }) => {
  const paid = isPaidOffer(offer);
  const isPublished = offer.status === 'active' && offer.is_feed_visible !== false;
  const compensation = paid ? offer.sponsor_amount : offer.value;
  const quantity = Number(offer.quantity || 0);
  const description = stripMarkup(offer.description) || journeyT('review.description_fallback');
  const terms = stripMarkup(offer.terms_and_conditions) || journeyT('review.terms_fallback');
  const isPaidAndFunded = paid && ['paid', 'partially_refunded', 'fully_refunded', 'disputed'].includes(offer.paid_collab_payment_status);
  const canEdit = offer.can_edit !== false && !isPaidAndFunded;
  const totalBudget = offer.total_budget || (Number(compensation || 0) * quantity);

  const featuredImage = offer.featured_image_url || offer.featured_image;
  const additionalImages = useMemo(() => {
    if (!Array.isArray(offer.additional_images)) return [];
    return offer.additional_images
      .map(img => (typeof img === 'string' ? img : (img?.url || img?.image || img?.file)))
      .filter(Boolean);
  }, [offer.additional_images]);

  const allImages = useMemo(() => {
    const list = [];
    if (featuredImage) list.push(featuredImage);
    additionalImages.forEach(img => {
      if (!list.includes(img)) list.push(img);
    });
    return list;
  }, [featuredImage, additionalImages]);

  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const currentPosterImage = allImages[activeImageIndex] || featuredImage;

  const handlePrevImage = () => {
    setActiveImageIndex(prev => (prev === 0 ? allImages.length - 1 : prev - 1));
  };
  const handleNextImage = () => {
    setActiveImageIndex(prev => (prev === allImages.length - 1 ? 0 : prev + 1));
  };

  return (
    <>
      <StepHeader eyebrow={`01 · ${journeyT('steps.review')}`} title={journeyT('review.title')} number={steps[0].number} />
      <section className={styles.frame}>
        <div className={styles.frameGrid}>
          <div className={styles.frameMain}>
            <div className={styles.collabHero}>
              <div className={styles.heroCopy}>
                <div className={styles.statusRow}>
                  <StatusPill tone={offer.status === 'active' ? 'success' : 'neutral'}>{offer.status_display || offer.status || journeyT('common.draft')}</StatusPill>
                  <span className={styles.category}>{offer.type_display || journeyT('common.collab')}</span>
                </div>
                <h2>{offer.title || journeyT('common.untitled_collab')}</h2>
                <p>{description}</p>
                <div className={styles.heroActions}>
                  {isPublished && <Button variant="outline" onClick={onCandidates}>{journeyT('review.view_candidates')}</Button>}
                  <Button
                    variant="outline"
                    onClick={onEdit}
                    aria-disabled={!canEdit}
                    className={!canEdit ? styles.btnLocked : undefined}
                    title={!canEdit ? (isPaidAndFunded ? journeyT('review.edit_locked_paid', { defaultValue: 'Las colaboraciones pagadas no pueden editarse una vez financiadas.' }) : journeyT('review.edit_locked', { defaultValue: 'Esta colaboración no se puede editar.' })) : undefined}
                  >
                    {!canEdit ? <LockKeyhole size={14} aria-hidden="true" /> : <Pencil size={15} aria-hidden="true" />}
                    {journeyT('review.edit')}
                  </Button>
                </div>
              </div>
              <div className={styles.posterWrapper}>
                <div className={styles.poster} aria-label={journeyT('common.workspace_aria')}>
                  <ImageWithFallback
                    className={styles.posterImage}
                    src={currentPosterImage}
                    alt={offer.title || journeyT('common.untitled_collab')}
                    name={offer.title || offer.business_name}
                  />
                  {allImages.length > 1 && (
                    <>
                      <button
                        type="button"
                        className={`${styles.posterNavButton} ${styles.posterNavPrev}`}
                        onClick={handlePrevImage}
                        aria-label={journeyT('review.prev_image', { defaultValue: 'Imagen anterior' })}
                      >
                        <ChevronLeft size={18} />
                      </button>
                      <button
                        type="button"
                        className={`${styles.posterNavButton} ${styles.posterNavNext}`}
                        onClick={handleNextImage}
                        aria-label={journeyT('review.next_image', { defaultValue: 'Siguiente imagen' })}
                      >
                        <ChevronRight size={18} />
                      </button>
                      <span className={styles.posterBadge}>
                        {activeImageIndex + 1} / {allImages.length}
                      </span>
                    </>
                  )}
                </div>
                {allImages.length > 1 && (
                  <div className={styles.posterThumbnails} role="group" aria-label={journeyT('review.thumbnails_aria', { defaultValue: 'Miniaturas de imágenes' })}>
                    {allImages.map((img, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className={`${styles.posterThumb} ${idx === activeImageIndex ? styles.activeThumb : ''}`}
                        onClick={() => setActiveImageIndex(idx)}
                        aria-label={journeyT('review.select_image', { index: idx + 1, defaultValue: `Seleccionar imagen ${idx + 1}` })}
                      >
                        <img src={img} alt="" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.metricStrip}>
              <div className={styles.compactMetric}><span>{journeyT('review.metrics.applications')}</span><strong>{offer.applications_count || 0}</strong></div>
              <div className={styles.compactMetric}><span>{journeyT('review.metrics.reach')}</span><strong>{Number(offer.viral_reach || 0).toLocaleString()}</strong></div>
              <div className={styles.compactMetric}><span>{journeyT('review.metrics.active')}</span><strong>{offer.active_collabs_count || 0}</strong></div>
            </div>
            <div className={`${styles.contentCard} ${styles.firstContentCard}`}>
              <div className={styles.sectionLabel}>{journeyT('review.description_label')}</div>
              <p>{description}</p>
            </div>
            <div className={styles.contentCard}>
              <div className={styles.sectionLabel}>{journeyT('review.terms_label')}</div>
              <p>{terms}</p>
            </div>
            {additionalImages.length > 0 && (
              <div className={styles.contentCard}>
                <div className={styles.sectionLabel}>{journeyT('review.gallery_label', { defaultValue: 'Galería de imágenes' })}</div>
                <div className={styles.galleryGrid}>
                  {allImages.map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`${styles.galleryCardItem} ${idx === activeImageIndex ? styles.galleryCardItemActive : ''}`}
                      onClick={() => setActiveImageIndex(idx)}
                      aria-label={journeyT('review.select_image', { index: idx + 1, defaultValue: `Seleccionar imagen ${idx + 1}` })}
                    >
                      <img src={img} alt={`Imagen ${idx + 1}`} />
                      {idx === 0 && <span className={styles.galleryTag}>{journeyT('review.featured_tag', { defaultValue: 'Portada' })}</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <aside className={styles.frameAside}>
            <div className={styles.sectionLabel}>{journeyT('common.summary')}</div>
            <div className={styles.facts}>
              <div className={styles.fact}><span>{journeyT('review.facts.location')}</span><strong>{offer.country_name || journeyT('common.empty')}</strong></div>
              <div className={styles.fact}><span>{journeyT('review.facts.schedule')}</span><strong>{formatDate(offer.from_date)} – {formatDate(offer.to_date)}</strong></div>
              <div className={styles.fact}><span>{journeyT('review.facts.audience')}</span><strong>{offer.followers_requirements_display || journeyT('review.facts.no_audience')}</strong></div>
              <div className={styles.fact}><span>{journeyT('review.facts.creators')}</span><strong>{quantity}</strong></div>
            </div>
            <div className={styles.moneyCard}>
              <span>{journeyT('review.money.per_creator')}</span>
              <strong>{formatMoney(compensation, offer.cash_currency || 'USD')}</strong>
              <span>{journeyT('review.money.total')}</span>
              <strong className={styles.moneySecondary}>{formatMoney(totalBudget, offer.cash_currency || 'USD')}</strong>
            </div>
          </aside>
        </div>
      </section>
      <div className={styles.footerActions}>
        <span className={styles.microcopy}>{paid ? journeyT('review.footer.funding_required') : isPublished ? journeyT('publish.footer.visible') : journeyT('review.footer.review_before_publish')}</span>
        <Button variant="primary" className={styles.nextButton} onClick={paid || isPublished ? goNext : onPublish} loading={!paid && publishing}>
          {paid ? journeyT('review.actions.fund') : isPublished ? journeyT('publish.actions.find_creators') : journeyT('publish.actions.publish')} <ArrowRight size={17} />
        </Button>
      </div>
    </>
  );
};

const FundingStep = ({ offer, payment, goNext, goBack, steps, onPaymentChange, subscriptionInfo, subscriptionChecked, onActivateSubscription }) => {
  const paymentStatus = payment?.payment_status || offer.paid_collab_payment_status || 'unfunded';
  const paid = TERMINAL_PAYMENT_STATES.has(paymentStatus);
  const hasActiveSubscription = Boolean(subscriptionInfo?.has_active_subscription);
  const subscriptionBlocked = subscriptionChecked && !hasActiveSubscription && !paid;
  const pricing = offer.paid_collab_pricing || {};
  const quantity = Number(offer.quantity || 0);
  const creatorTotal = pricing.creator_compensation_total || (Number(offer.sponsor_amount || 0) * quantity);
  const platformFees = pricing.foth_service_fee_amount || 0;
  const payoutFees = pricing.payout_fee_amount || 0;
  return (
    <>
      <StepHeader eyebrow={`02 · ${journeyT('steps.funding')}`} title={journeyT('funding.title')} number={steps.find((step) => step.id === 'funding')?.number} />
      <section className={styles.frame}>
        <div className={styles.frameGrid}>
          <div className={styles.frameMain}>
            <div className={styles.sectionLabel}>{journeyT('funding.eyebrow')}</div>
            <h2 className={styles.fundingTitle}>{journeyT('funding.heading')}</h2>
            <p className={styles.fundingIntro}>{journeyT('funding.intro')}</p>
            <div className={styles.fundingSummary}>
              <div className={styles.payRow}><span>{journeyT('funding.summary.creator_total')}</span><strong>{formatMoney(creatorTotal, offer.cash_currency || 'USD')}</strong></div>
              <div className={styles.payRow}><span>{journeyT('funding.summary.fees')}</span><strong>{formatMoney(Number(platformFees) + Number(payoutFees), offer.cash_currency || 'USD')}</strong></div>
              <small>{journeyT('funding.summary.stripe_note')}</small>
            </div>
            {!subscriptionChecked ? (
              <div className={styles.subscriptionChecking} role="status">
                <RefreshCw size={18} className={styles.spin} />
                <span>{journeyT('funding.subscription.checking')}</span>
              </div>
            ) : subscriptionBlocked ? (
              <div className={styles.subscriptionGate} role="status">
                <div className={styles.subscriptionGateIcon}><LockKeyhole size={21} /></div>
                <div className={styles.subscriptionGateCopy}>
                  <span className={styles.sectionLabel}>{journeyT('funding.subscription.eyebrow')}</span>
                  <h3>{journeyT('funding.subscription.title')}</h3>
                  <p>{journeyT('funding.subscription.body', { amount: formatMoney(creatorTotal, offer.cash_currency || 'USD') })}</p>
                  <div className={styles.subscriptionGateActions}>
                    <Button variant="primary" onClick={onActivateSubscription}>
                      {journeyT('funding.subscription.action')} <ArrowRight size={16} />
                    </Button>
                    <Button variant="ghost" onClick={goBack}>{journeyT('funding.subscription.secondary')}</Button>
                  </div>
                </div>
              </div>
            ) : (
              <PaidCollabPaymentStatus collabId={offer.id} role="business" collab={offer} onPaymentChange={onPaymentChange} />
            )}
          </div>
          <aside className={styles.frameAside}>
            <div className={styles.sectionLabel}>{journeyT('funding.after.label')}</div>
            <div className={styles.contentCard}><h3>{journeyT('funding.after.protected_title')}</h3><p>{journeyT('funding.after.protected_body')}</p></div>
            <div className={styles.contentCard}><h3>{journeyT('funding.after.direct_title')}</h3><p>{journeyT('funding.after.direct_body')}</p></div>
            <div className={styles.moneyCard}><span>{journeyT('funding.summary.total_for_creators', { count: quantity })}</span><strong>{formatMoney(creatorTotal, offer.cash_currency || 'USD')}</strong><span>{journeyT('funding.summary.business_total')}</span><strong className={styles.moneySecondary}>{formatMoney(Number(creatorTotal) + Number(platformFees) + Number(payoutFees), offer.cash_currency || 'USD')}</strong></div>
            <div className={styles.fundingState}><StatusPill tone={paid ? 'success' : 'warning'}>{journeyT(`paymentStatus.${PAYMENT_STATUS_KEYS[paymentStatus] || 'unfunded'}`)}</StatusPill><span>{paid ? journeyT('funding.confirmed') : journeyT('funding.pending')}</span></div>
          </aside>
        </div>
      </section>
      <div className={styles.footerActions}>
        <Button variant="ghost" onClick={goBack}>← {journeyT('common.back')}</Button>
        <Button variant="primary" className={styles.nextButton} onClick={goNext} disabled={!paid}>{journeyT('funding.actions.confirm')} <ArrowRight size={17} /></Button>
      </div>
    </>
  );
};

const PublishStep = ({ offer, payment, onPublish, publishing, goNext, steps }) => {
  const isPublished = offer.status === 'active' && offer.is_feed_visible !== false;
  const paid = isPaidOffer(offer);
  const funded = !paid || TERMINAL_PAYMENT_STATES.has(payment?.payment_status || offer.paid_collab_payment_status);
  const quantity = Number(offer.quantity || 0);
  const creatorTotal = offer.paid_collab_pricing?.creator_compensation_total
    || (Number(offer.sponsor_amount || 0) * quantity);
  const currency = offer.cash_currency || 'USD';
  const dateRange = offer.from_date && offer.to_date
    ? `${formatDate(offer.from_date)} – ${formatDate(offer.to_date)}`
    : journeyT('publish.dates_pending');
  return (
    <>
      <StepHeader eyebrow={`${steps.find((step) => step.id === 'publish')?.number} · ${journeyT('steps.publish')}`} title={isPublished ? journeyT('publish.published_title') : journeyT('publish.title')} number={steps.find((step) => step.id === 'publish')?.number} />
      <section className={`${styles.frame} ${styles.publicationFrame}`}>
        <div className={styles.liveSplash}>
          <div className={styles.liveSplashContent}>
            <div className={styles.liveOrb}>{isPublished ? <Check size={30} strokeWidth={2.5} /> : <Megaphone size={28} />}</div>
            <div className={styles.statusRow}>
              <StatusPill tone={isPublished && paid ? 'success' : isPublished ? 'neutral' : 'warning'}>{isPublished && paid ? journeyT('publish.status.paid') : isPublished ? journeyT('publish.status.published') : funded ? journeyT('publish.status.ready') : journeyT('publish.status.funding_pending')}</StatusPill>
              <StatusPill tone={isPublished ? 'neutral' : 'warning'}>{isPublished ? journeyT('publish.status.feed_active') : journeyT('publish.status.feed_hidden')}</StatusPill>
            </div>
            <h2>{isPublished ? journeyT('publish.heading_published', { title: offer.title }) : journeyT('publish.heading_ready', { title: offer.title })}</h2>
            <p>
              {isPublished
                ? paid
                  ? journeyT('publish.body.funded', { amount: formatMoney(creatorTotal, currency), count: quantity })
                  : journeyT('publish.body.visible')
                : funded
                  ? journeyT('publish.body.ready')
                  : journeyT('publish.body.funding_required')}
            </p>
            <div className={styles.liveMeta}>
              <span className={styles.liveMetaPill}>{offer.country_name || journeyT('publish.location_pending')}</span>
              <span className={styles.liveMetaPill}>{journeyT(quantity === 1 ? 'publish.creator_count_one' : 'publish.creator_count_other', { count: quantity })}</span>
              <span className={styles.liveMetaPill}>{dateRange}</span>
            </div>
          </div>
        </div>
      </section>
      <div className={styles.footerActions}>
        <span className={styles.microcopy}>{isPublished ? journeyT('publish.footer.visible') : funded ? journeyT('publish.footer.activate') : journeyT('publish.footer.funding_required')}</span>
        {isPublished
          ? <Button variant="primary" className={styles.nextButton} onClick={goNext}>{journeyT('publish.actions.find_creators')} <ArrowRight size={17} /></Button>
          : <Button variant="primary" className={styles.nextButton} onClick={onPublish} loading={publishing} disabled={!funded}>{journeyT('publish.actions.publish')} <Megaphone size={17} /></Button>}
      </div>
    </>
  );
};

const invitationStatusLabel = (status) => journeyT(`creators.invitation_status.${status || 'pending'}`);

const CandidateRow = ({ entry, busy, onApprove, onReject, onReviewDeliverable }) => {
  const isApplication = entry.kind === 'application';
  const isInvitation = !isApplication;
  const application = entry.application;
  const invite = entry.invitation;
  const status = isInvitation ? invite.status : application.status;
  const selected = isInvitation ? status === 'accepted' : ['accepted', 'completed'].includes(status);
  const hasDeliverable = !isInvitation && getApplicationDeliverables(application).length > 0;
  const rejected = ['rejected', 'declined', 'revoked', 'expired'].includes(status);
  const pending = !isInvitation && status === 'applied';
  const name = isInvitation
    ? invite.creator_full_name || invite.creator_username || invite.email
    : getApplicationCreatorName(application);
  const username = isInvitation
    ? invite.creator_username ? `@${invite.creator_username}` : invite.email
    : `@${getApplicationUsername(application) || 'creator'}`;
  const statusLabel = isInvitation
    ? invitationStatusLabel(status)
    : hasDeliverable ? journeyT('creators.status.delivery_received')
      : status === 'accepted' ? journeyT('creators.status.selected')
        : status === 'completed' ? journeyT('creators.status.completed')
          : status === 'rejected' ? journeyT('creators.status.rejected') : journeyT('creators.status.pending');
  const originLabel = entry.origin === 'invitation' ? journeyT('creators.invitation') : journeyT('creators.application');
  const originDate = formatDate(application?.created_at || invite?.created_at);
  return (
    <article className={styles.candidateRow}>
      <div className={styles.candidateCreator}>
        <Avatar size="md" src={isApplication ? application?.creator_profile_image : invite.creator_profile_image} name={name} alt={name} />
        <div className={styles.candidateIdentity}><strong>{name}</strong><span>{username}</span></div>
      </div>
      <div className={styles.candidateOrigin}><span>{originLabel}</span><small>{originDate}</small></div>
      <div className={styles.candidateState}><StatusPill tone={hasDeliverable && status !== 'completed' ? 'warning' : selected ? 'success' : rejected ? 'danger' : pending ? 'warning' : 'neutral'}>{statusLabel}</StatusPill></div>
      <div className={styles.rowActions}>
        {pending && <>
          <Button variant="outline" size="sm" onClick={() => onApprove(application)} loading={busy}><Check size={14} /> {journeyT('creators.actions.select')}</Button>
          <Button variant="ghost" size="sm" onClick={() => onReject(application)} disabled={busy}><X size={14} /> {journeyT('creators.actions.reject')}</Button>
        </>}
        {selected && !hasDeliverable && <span className={styles.actionNote}>{journeyT('creators.capacity_confirmed')}</span>}
        {hasDeliverable && <Button variant="outline" size="sm" onClick={() => onReviewDeliverable(application)}><FileCheck2 size={14} /> {journeyT('creators.actions.review_delivery')}</Button>}
        {isInvitation && !selected && !rejected && <span className={styles.actionNote}>{journeyT('creators.waiting_response')}</span>}
      </div>
    </article>
  );
};

const CreatorsStep = ({ offer, applications, invitations, directInvitations, onApprove, onReject, onSendInvites, onFindCreators, onReviewDeliverable, processing, sendingInvites, goBack, goNext, steps }) => {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [inviteEmails, setInviteEmails] = useState('');
  const selectedApplications = applications.filter((application) => ['accepted', 'completed'].includes(application.status));
  const acceptedInvitations = invitations.filter((invite) => invite.status === 'accepted');
  const pendingApplications = applications.filter((application) => application.status === 'applied');
  const capacity = Number(offer.quantity || 0);
  const covered = getCoveredCreatorCount(offer, applications);
  const available = Math.max(capacity - covered, 0);
  const hasDeliverableToReview = applications.some((application) => ['accepted', 'completed'].includes(application.status) && getApplicationDeliverables(application).length > 0);
  const canContinueToDeliverables = (covered >= capacity && capacity > 0) || hasDeliverableToReview;
  const candidateEntries = useMemo(() => {
    // 1. Applications: link with any matching direct or recipient invitation to preserve origin
    const appEntries = applications.map((application) => {
      const matchingDirect = directInvitations.find((di) => (
        di.creator_user_id && String(di.creator_user_id) === String(application.creator)
      ));
      const matchingRecipient = invitations.find((ri) => (
        (ri.creator_user_id && String(ri.creator_user_id) === String(application.creator))
        || (ri.email && application.creator_email && ri.email.toLowerCase() === application.creator_email.toLowerCase())
      ));
      const linkedInvitation = matchingDirect || matchingRecipient;
      return {
        kind: 'application',
        id: `application-${application.id}`,
        application,
        invitation: linkedInvitation,
        origin: linkedInvitation ? 'invitation' : 'application',
      };
    });

    // 2. Recipient invitations: exclude those already materialized as an application
    const unlinkedRecipient = invitations
      .filter((ri) => !applications.some((app) => (
        (ri.creator_user_id && String(ri.creator_user_id) === String(app.creator))
        || (ri.email && app.creator_email && ri.email.toLowerCase() === app.creator_email.toLowerCase())
      )))
      .map((invitation) => ({
        kind: 'invitation',
        id: `invitation-${invitation.id}`,
        invitation,
        origin: 'invitation',
      }));

    // 3. Direct invitations: exclude those already materialized as an application
    const unlinkedDirect = directInvitations
      .filter((di) => !applications.some((app) => (
        di.creator_user_id && String(di.creator_user_id) === String(app.creator)
      )))
      .map((invitation) => ({
        kind: 'direct_invitation',
        id: `direct-invitation-${invitation.id}`,
        invitation,
        origin: 'invitation',
      }));

    return [...appEntries, ...unlinkedRecipient, ...unlinkedDirect];
  }, [applications, invitations, directInvitations]);

  const normalizedQuery = query.trim().toLowerCase();
  const visibleEntries = candidateEntries.filter((entry) => {
    const isInvitation = entry.kind !== 'application';
    const item = isInvitation ? entry.invitation : entry.application;
    const name = isInvitation
      ? `${item.creator_full_name || ''} ${item.creator_username || ''} ${item.email || ''}`
      : `${getApplicationCreatorName(item)} ${getApplicationUsername(item)} ${item.creator_email || ''}`;
    const matchesQuery = !normalizedQuery || name.toLowerCase().includes(normalizedQuery);
    const isSelected = isInvitation ? item.status === 'accepted' : ['accepted', 'completed'].includes(item.status);
    const isPending = isInvitation ? ['pending', 'opened'].includes(item.status) : item.status === 'applied';
    const isInvited = entry.origin === 'invitation';
    const matchesFilter = filter === 'all'
      || (filter === 'pending' && isPending)
      || (filter === 'selected' && isSelected)
      || (filter === 'invited' && isInvited);
    return matchesQuery && matchesFilter;
  });
  const firstSelected = selectedApplications[0]
    ? getApplicationCreatorName(selectedApplications[0])
    : acceptedInvitations[0]?.email || directInvitations.find((invite) => invite.status === 'accepted')?.creator_username;
  const selectionHint = covered === 0
    ? journeyT('creators.selection.none')
    : `${firstSelected ? journeyT('creators.selection.one', { name: firstSelected }) : journeyT('creators.selection.covered', { count: covered })} ${available ? journeyT('creators.selection.remaining', { count: available }) : journeyT('creators.selection.complete')}`;
  const invitationsOpen = offer.status === 'active' && offer.is_feed_visible !== false && available > 0;
  const allInvitations = [...invitations, ...directInvitations];
  const pendingInvitations = allInvitations.filter((invite) => ['pending', 'opened'].includes(invite.status)).length;
  const sendInvites = async () => {
    const sent = await onSendInvites(inviteEmails);
    if (sent) setInviteEmails('');
  };
  return (
    <>
      <StepHeader eyebrow={`${steps.find((step) => step.id === 'creators')?.number} · ${journeyT('steps.creators')}`} title={journeyT('creators.title')} description={journeyT('creators.description')} number={steps.find((step) => step.id === 'creators')?.number} />
      <section className={styles.creatorFrame}>
        <div className={styles.creatorFrameGrid}>
          <div className={styles.creatorFrameMain}>
            <div className={styles.creatorTop}>
              <div className={styles.statusRow}><StatusPill tone="success">{journeyT('publish.status.feed_active')}</StatusPill><StatusPill>{journeyT('creators.positions', { count: capacity })}</StatusPill><StatusPill tone={covered >= capacity && capacity > 0 ? 'success' : 'neutral'}>{journeyT(covered === 1 ? 'creators.covered_one' : 'creators.covered_other', { count: covered })}</StatusPill></div>
              <span className={styles.positionsCompact}>{available ? journeyT('creators.available', { count: available }) : journeyT('creators.all_covered')}</span>
            </div>

            <div className={styles.selectionSummary}>
              <div><strong>{journeyT('creators.covered_summary', { covered, capacity })}</strong><span>{selectionHint}</span></div>
              <div className={styles.capacity}><i style={{ width: `${capacity ? Math.min(100, (covered / capacity) * 100) : 0}%` }} /></div>
            </div>

            <div className={styles.pipelineStats} aria-label={journeyT('creators.stats_aria')}>
              <div className={styles.pStat}><span>{journeyT('creators.stats.candidates')}</span><strong>{candidateEntries.length}</strong><small>{journeyT('creators.stats.in_progress')}</small></div>
              <div className={styles.pStat}><span>{journeyT('creators.stats.pending')}</span><strong>{pendingApplications.length}</strong><small>{journeyT('creators.stats.to_decide')}</small></div>
              <div className={styles.pStat}><span>{journeyT('creators.stats.selected')}</span><strong>{covered}</strong><small>{journeyT('creators.stats.of', { count: capacity })}</small></div>
              <div className={styles.pStat}><span>{journeyT('creators.stats.invited')}</span><strong>{allInvitations.length}</strong><small>{journeyT('creators.stats.pending_count', { count: pendingInvitations })}</small></div>
            </div>

            <div className={styles.pipelineHeader}>
              <div><div className={styles.sectionLabel}>{journeyT('creators.review_label')}</div><h3>{journeyT('creators.candidates')}</h3><p>{journeyT('creators.candidates_description')}</p></div>
            </div>

            <div className={styles.pipelineToolbar}>
              <label className={styles.searchField}><Search size={15} aria-hidden="true" /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={journeyT('creators.search_placeholder')} aria-label={journeyT('creators.search_aria')} /></label>
              <div className={styles.filterChips} role="group" aria-label={journeyT('creators.filter_aria')}>
                {[['all', journeyT('creators.filters.all')], ['pending', journeyT('creators.filters.pending')], ['selected', journeyT('creators.filters.selected')], ['invited', journeyT('creators.filters.invited')]].map(([value, label]) => <button key={value} className={`${styles.chipBtn} ${filter === value ? styles.activeChip : ''}`} type="button" onClick={() => setFilter(value)}>{label}</button>)}
              </div>
            </div>

            <div className={styles.candidateTable}>
              <div className={styles.candidateHead}><div>{journeyT('creators.table.creator')}</div><div>{journeyT('creators.table.origin')}</div><div>{journeyT('creators.table.status')}</div><div className={styles.candidateActionsHead}>{journeyT('creators.table.actions')}</div></div>
              <div className={styles.candidateTableBody}>
                {visibleEntries.map((entry) => <CandidateRow key={entry.id} entry={entry} busy={processing === entry.application?.id} onApprove={onApprove} onReject={onReject} onReviewDeliverable={onReviewDeliverable} />)}
                {!visibleEntries.length && <div className={styles.candidateEmpty}><Users size={20} /><span>{journeyT('creators.empty')}</span></div>}
              </div>
            </div>

            <div className={styles.positionNote}><div className={styles.sectionLabel}>{journeyT('creators.positions_label')}</div><p>{covered ? `${journeyT(covered === 1 ? 'creators.covered_one' : 'creators.covered_other', { count: covered })}${firstSelected ? `: ${firstSelected}.` : '.'}` : journeyT('creators.no_positions')}</p></div>
          </div>

          <aside className={styles.creatorFrameAside}>
            <div className={styles.invitePanel}>
              <div className={styles.sectionLabel}><Mail size={13} /> {journeyT('creators.invites.label')}</div>
              <h3>{journeyT('creators.invites.title')}</h3>
              <p>{journeyT('creators.invites.description')}</p>
              <textarea value={inviteEmails} onChange={(event) => setInviteEmails(event.target.value)} placeholder={'ana@email.com\nleo@email.com'} disabled={!invitationsOpen} aria-label={journeyT('creators.invites.email_aria')} />
              <Button variant="primary" className={styles.invitePrimary} onClick={sendInvites} loading={sendingInvites} disabled={!invitationsOpen || !inviteEmails.trim()}><Send size={15} /> {journeyT('creators.invites.send')}</Button>
              {!invitationsOpen && <small className={styles.inviteClosed}>{journeyT('creators.invites.closed')}</small>}
              <div className={styles.inviteInfo}><strong>{journeyT('creators.invites.how_it_works')}</strong><div className={styles.inviteRule}><i>1</i><p><b>{journeyT('creators.invites.application_label')}:</b> {journeyT('creators.invites.application_body')}</p></div><div className={styles.inviteRule}><i>2</i><p><b>{journeyT('creators.invites.invitation_label')}:</b> {journeyT('creators.invites.invitation_body')}</p></div></div>
              {allInvitations.length > 0 && <div className={styles.inviteStack}>{allInvitations.slice(0, 4).map((invite) => <div key={`${invite.source || 'recipient'}-${invite.id}`} className={styles.inviteStackRow}><div><strong>{invite.creator_username ? `@${invite.creator_username}` : invite.creator_full_name || invite.email}</strong><small>{invitationStatusLabel(invite.status)}</small></div><StatusPill tone={invite.status === 'accepted' ? 'success' : ['declined', 'revoked', 'expired'].includes(invite.status) ? 'danger' : 'warning'}>{invitationStatusLabel(invite.status)}</StatusPill></div>)}</div>}
              <Button variant="outline" className={styles.inviteSearchBtn} onClick={onFindCreators} disabled={!invitationsOpen}><UserPlus size={15} /> {journeyT('creators.invites.find')}</Button>
            </div>
          </aside>
        </div>
      </section>
      <div className={styles.footerActions}><Button variant="ghost" onClick={goBack}>← {journeyT('common.back')}</Button><span className={styles.microcopy}>{covered >= capacity && capacity > 0 ? journeyT('creators.footer.all_covered') : hasDeliverableToReview ? journeyT('creators.footer.delivery_ready') : journeyT('creators.footer.cover_first')}</span><Button variant="primary" className={styles.nextButton} onClick={goNext} disabled={!canContinueToDeliverables}>{journeyT('creators.actions.continue')} <ArrowRight size={17} /></Button></div>
    </>
  );
};

export const DeliverablesStep = ({ offer, applications, initialApplicationUsername, onComplete, processing, goBack, goNext, steps }) => {
  const [activeApplicationId, setActiveApplicationId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeDeliverableIndex, setActiveDeliverableIndex] = useState(0);
  const detailPanelRef = useRef(null);

  const accepted = applications.filter((application) => ['accepted', 'completed'].includes(application.status));
  const ready = accepted.filter((application) => application.status === 'accepted' && getDeliverableProgress(offer, application).isComplete);
  const completed = accepted.filter((application) => application.status === 'completed');
  const received = accepted.filter((application) => getApplicationDeliverables(application).length > 0);
  const activeApplication = accepted.find((application) => application.id === activeApplicationId) || accepted[0] || null;
  const activeDeliverables = getApplicationDeliverables(activeApplication);
  const safeDeliverableIndex = Math.min(
    Math.max(0, activeDeliverableIndex),
    Math.max(0, activeDeliverables.length - 1)
  );
  const activeDeliverable = activeDeliverables[safeDeliverableIndex] || null;
  const activeProgress = getDeliverableProgress(offer, activeApplication);
  const activeInsights = getDeliverableInsights(activeDeliverable);
  const activeIsApproved = activeApplication?.status === 'completed';
  const compensation = isPaidOffer(offer) ? offer.sponsor_amount : offer.value;
  const selectedReady = ready.filter((application) => selectedIds.includes(application.id));
  const allApproved = accepted.length > 0 && completed.length === accepted.length;

  useEffect(() => {
    setActiveDeliverableIndex(0);
  }, [activeApplicationId]);

  useEffect(() => {
    const currentAccepted = applications.filter((application) => ['accepted', 'completed'].includes(application.status));
    const currentReady = currentAccepted.filter((application) => application.status === 'accepted' && getDeliverableProgress(offer, application).isComplete);
    const requestedApplication = initialApplicationUsername
      ? currentAccepted.find((application) => getApplicationUsername(application).toLowerCase() === initialApplicationUsername.toLowerCase())
      : null;
    if (!activeApplicationId && (requestedApplication || currentReady[0])) setActiveApplicationId((requestedApplication || currentReady[0]).id);
    if (activeApplicationId && !currentAccepted.some((application) => application.id === activeApplicationId)) setActiveApplicationId(currentReady[0]?.id || currentAccepted[0]?.id || null);
    setSelectedIds((current) => {
      const next = current.filter((applicationId) => currentReady.some((application) => application.id === applicationId));
      return next.length === current.length ? current : next;
    });
  }, [applications, activeApplicationId, initialApplicationUsername, offer]);

  const handleSelectApplication = (applicationId, deliverableIndex = 0) => {
    setActiveApplicationId(applicationId);
    setActiveDeliverableIndex(deliverableIndex);
    if (typeof window !== 'undefined' && window.innerWidth < 992 && detailPanelRef.current) {
      detailPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const toggleSelected = (applicationId) => setSelectedIds((current) => current.includes(applicationId) ? current.filter((id) => id !== applicationId) : [...current, applicationId]);
  const approveOne = async (application) => {
    const completedApplication = await onComplete(application);
    if (completedApplication) setSelectedIds((current) => current.filter((id) => id !== application.id));
  };
  const approveSelected = async () => {
    for (const application of selectedReady) await approveOne(application);
  };
  const selectPending = () => setSelectedIds(ready.map((application) => application.id));

  return (
    <>
      <StepHeader eyebrow={`${steps.find((step) => step.id === 'deliverables')?.number} · ${journeyT('steps.deliverables')}`} title={journeyT('deliverables.title')} number={steps.find((step) => step.id === 'deliverables')?.number} />
      <section className={styles.deliverablesFrame}>
        <div className={styles.deliverablesFrameMain}>
          <div className={styles.deliverySummary}>
            <div className={styles.deliverySummaryCopy}><div className={styles.infoIcon}>i</div><div><strong>{journeyT('deliverables.summary.title')}</strong><p>{journeyT('deliverables.summary.body')}</p></div></div>
            <div className={styles.deliverySummaryStats}><div><span>{journeyT('deliverables.summary.received')}</span><strong>{received.length}/{accepted.length}</strong></div><div><span>{journeyT('deliverables.summary.approved')}</span><strong>{completed.length}/{accepted.length}</strong></div><div><span>{journeyT('deliverables.summary.processing')}</span><strong>{completed.length}</strong></div></div>
          </div>

          <div className={styles.deliverablesLayout}>
            <section className={styles.deliveryListPanel} aria-label={journeyT('deliverables.received_aria')}>
              <div className={styles.deliveryListHeader}><div><h3>{journeyT('deliverables.received_title')}</h3><p>{journeyT('deliverables.received_description')}</p></div><StatusPill tone={ready.length ? 'warning' : allApproved ? 'success' : 'neutral'}>{journeyT(ready.length === 1 ? 'deliverables.pending_one' : 'deliverables.pending_other', { count: ready.length })}</StatusPill></div>
              <div className={styles.deliveryColumnHead}><div /><div>{journeyT('creators.table.creator')}</div><div>{journeyT('deliverables.content')}</div><div>{journeyT('creators.table.status')}</div></div>
              <div className={styles.deliveryRows}>
                {accepted.map((application) => {
                  const deliverables = getApplicationDeliverables(application);
                  const hasContent = deliverables.length > 0;
                  const done = application.status === 'completed';
                  const isActive = activeApplication?.id === application.id;
                  return <article key={application.id} className={`${styles.deliveryCandidateRow} ${isActive ? styles.activeDeliveryRow : ''} ${done ? styles.approvedDeliveryRow : ''}`} onClick={() => handleSelectApplication(application.id, 0)}>
                    <input type="checkbox" checked={selectedIds.includes(application.id)} disabled={!hasContent || done} onChange={() => toggleSelected(application.id)} onClick={(event) => event.stopPropagation()} aria-label={journeyT('deliverables.select_creator', { name: getApplicationCreatorName(application) })} />
                    <div className={styles.deliveryCreator}><Avatar size="md" src={application.creator_profile_image} name={getApplicationCreatorName(application)} alt="" /><div><strong>{getApplicationCreatorName(application)}</strong><span>@{getApplicationUsername(application) || 'creator'}</span></div></div>
                    <div className={styles.deliveryContentMeta}>
                      <strong>{hasContent && getDeliverableProgress(offer, application).requiredTotal !== null ? `${getDeliverableProgress(offer, application).submittedTotal}/${getDeliverableProgress(offer, application).requiredTotal} entregables` : hasContent ? journeyT(deliverables.length === 1 ? 'deliverables.count_one' : 'deliverables.count_other', { count: deliverables.length }) : journeyT('deliverables.no_content')}</strong>
                      <span>{hasContent ? deliverables.map((d) => getDeliverableType(d)).join(' · ') : journeyT('deliverables.awaiting')}</span>
                      {deliverables.length > 1 && (
                        <div className={styles.rowDeliverableThumbs}>
                          {deliverables.map((d, dIdx) => (
                            <button
                              key={d.id || dIdx}
                              type="button"
                              className={`${styles.rowDeliverableChip} ${isActive && dIdx === safeDeliverableIndex ? styles.activeRowDeliverableChip : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectApplication(application.id, dIdx);
                              }}
                              title={`Ver ${getDeliverableType(d)}`}
                            >
                              {getDeliverableType(d)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className={styles.deliveryStatus}><StatusPill tone={done ? 'success' : hasContent ? 'warning' : 'neutral'}>{done ? journeyT('deliverables.status.approved') : hasContent ? journeyT('deliverables.status.pending') : journeyT('deliverables.status.not_submitted')}</StatusPill></div>
                  </article>;
                })}
                {!accepted.length && <div className={styles.deliveryEmpty}><FileCheck2 size={20} /><span>{journeyT('deliverables.empty')}</span></div>}
              </div>
              <div className={styles.deliveryBulk}><div><strong>{selectedIds.length ? journeyT(selectedIds.length === 1 ? 'deliverables.selected_one' : 'deliverables.selected_other', { count: selectedIds.length }) : journeyT('deliverables.none_selected')}</strong><span>{journeyT('deliverables.bulk_help')}</span></div><div className={styles.deliveryBulkActions}><Button variant="outline" size="sm" onClick={selectPending} disabled={!ready.length}>{journeyT('deliverables.actions.select_pending')}</Button><Button variant="primary" size="sm" onClick={approveSelected} loading={Boolean(processing) && selectedIds.length > 0} disabled={!selectedReady.length}>{journeyT('deliverables.actions.approve_selected')} <Check size={14} /></Button></div></div>
            </section>

            <aside ref={detailPanelRef} className={styles.deliveryDetailPanel} aria-label={journeyT('deliverables.detail_aria')}>
              <div className={styles.deliveryDetailHeader}>
                <div>
                  <div className={styles.sectionLabel}>{journeyT('deliverables.selected_label')}</div>
                  <h3>{activeApplication ? `@${getApplicationUsername(activeApplication) || getApplicationCreatorName(activeApplication)}` : journeyT('deliverables.no_selection')}</h3>
                </div>
                <div className={styles.deliveryDetailHeaderBadges}>
                  {activeDeliverables.length > 0 && (
                    <span className={styles.deliverableCountBadge}>
                      {activeDeliverables.length === 1
                        ? journeyT('deliverables.count_one', { count: 1 })
                        : journeyT('deliverables.count_other', { count: activeDeliverables.length })}
                    </span>
                  )}
                  <StatusPill tone={activeIsApproved ? 'success' : activeDeliverable ? 'warning' : 'neutral'}>
                    {activeIsApproved ? journeyT('deliverables.status.approved') : activeDeliverable ? journeyT('deliverables.status.pending') : journeyT('deliverables.status.not_submitted')}
                  </StatusPill>
                </div>
              </div>

              <div className={styles.deliveryDetailBody}>
                {activeDeliverables.length > 1 && (
                  <>
                    <div className={styles.deliverablesTabBar} role="tablist" aria-label="Entregables del creador">
                      {activeDeliverables.map((deliverable, index) => {
                        const isSelected = index === safeDeliverableIndex;
                        const typeLabel = getDeliverableType(deliverable);
                        return (
                          <button
                            key={deliverable.id || index}
                            type="button"
                            role="tab"
                            aria-selected={isSelected}
                            className={`${styles.deliverableTab} ${isSelected ? styles.activeDeliverableTab : ''}`}
                            onClick={() => setActiveDeliverableIndex(index)}
                          >
                            <span className={styles.deliverableTabNumber}>{index + 1}</span>
                            <span className={styles.deliverableTabLabel}>{typeLabel}</span>
                          </button>
                        );
                      })}
                    </div>

                    <div className={styles.deliverableThumbnailsStrip} aria-label="Miniaturas de entregables">
                      {activeDeliverables.map((deliverable, index) => {
                        const isSelected = index === safeDeliverableIndex;
                        const thumbUrl = deliverable.thumbnail_url || deliverable.media_url;
                        const typeLabel = getDeliverableType(deliverable);
                        return (
                          <button
                            key={deliverable.id || index}
                            type="button"
                            className={`${styles.deliverableThumbItem} ${isSelected ? styles.activeDeliverableThumb : ''}`}
                            onClick={() => setActiveDeliverableIndex(index)}
                            aria-label={`${typeLabel} (${index + 1}/${activeDeliverables.length})`}
                            title={`${typeLabel} (${index + 1} de ${activeDeliverables.length})`}
                          >
                            {thumbUrl ? (
                              <img src={thumbUrl} alt={typeLabel} />
                            ) : (
                              <div className={styles.deliverableThumbFallback}>
                                <span>{index + 1}</span>
                              </div>
                            )}
                            <span className={styles.deliverableThumbTag}>{typeLabel}</span>
                            {isSelected && <span className={styles.deliverableThumbSelectedDot} />}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}

                <div className={styles.deliveryPreviewWrapper}>
                  {activeDeliverable?.thumbnail_url || activeDeliverable?.media_url ? (
                    <img className={styles.deliveryPreviewImage} src={activeDeliverable.thumbnail_url || activeDeliverable.media_url} alt={journeyT('deliverables.preview_alt')} />
                  ) : (
                    <div className={styles.deliveryPreview}>
                      {journeyT('deliverables.preview')}<br />
                      {activeDeliverable ? getDeliverableType(activeDeliverable) : journeyT('deliverables.content')}
                    </div>
                  )}
                  {activeDeliverables.length > 1 && (
                    <>
                      <button
                        type="button"
                        className={`${styles.deliverableNavButton} ${styles.deliverableNavPrev}`}
                        onClick={() => setActiveDeliverableIndex((prev) => (prev > 0 ? prev - 1 : activeDeliverables.length - 1))}
                        aria-label={journeyT('deliverables.prev_deliverable', { defaultValue: 'Entrega anterior' })}
                        title={journeyT('deliverables.prev_deliverable', { defaultValue: 'Entrega anterior' })}
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <button
                        type="button"
                        className={`${styles.deliverableNavButton} ${styles.deliverableNavNext}`}
                        onClick={() => setActiveDeliverableIndex((prev) => (prev < activeDeliverables.length - 1 ? prev + 1 : 0))}
                        aria-label={journeyT('deliverables.next_deliverable', { defaultValue: 'Siguiente entrega' })}
                        title={journeyT('deliverables.next_deliverable', { defaultValue: 'Siguiente entrega' })}
                      >
                        <ChevronRight size={20} />
                      </button>
                      <span className={styles.deliverableCounterBadge}>
                        {journeyT('deliverables.deliverable_of', {
                          current: safeDeliverableIndex + 1,
                          total: activeDeliverables.length,
                          defaultValue: `${safeDeliverableIndex + 1} / ${activeDeliverables.length}`,
                        })}
                      </span>
                    </>
                  )}
                </div>

                <div className={styles.deliveryMetaRow}>
                  <span className={styles.deliveryPill}>{activeApplication ? `@${getApplicationUsername(activeApplication) || getApplicationCreatorName(activeApplication)}` : journeyT('common.empty')}</span>
                  <span className={`${styles.deliveryPill} ${styles.deliveryPillHighlight}`}>{activeDeliverable ? getDeliverableType(activeDeliverable) : journeyT('deliverables.content')}</span>
                  {activeDeliverables.length > 1 && (
                    <span className={styles.deliveryPill}>
                      {journeyT('deliverables.deliverable_of', {
                        current: safeDeliverableIndex + 1,
                        total: activeDeliverables.length,
                        defaultValue: `Entrega ${safeDeliverableIndex + 1} de ${activeDeliverables.length}`,
                      })}
                    </span>
                  )}
                  <span className={styles.deliveryPill}>{activeDeliverable ? formatDate(activeDeliverable.created_at) : journeyT('common.empty')}</span>
                </div>

                <div className={styles.deliveryMetrics}>
                  <div><span>{journeyT('deliverables.metrics.views')}</span><strong>{formatMetric(activeInsights.video_views ?? activeInsights.views)}</strong></div>
                  <div><span>{journeyT('deliverables.metrics.reach')}</span><strong>{formatMetric(activeInsights.reach)}</strong></div>
                  <div><span>{journeyT('deliverables.metrics.engagement')}</span><strong>{formatPercent(activeInsights.engagement_rate)}</strong></div>
                </div>

                {activeDeliverables.length > 1 && (
                  <div className={styles.allDeliverablesSection}>
                    <div className={styles.allDeliverablesTitle}>
                      <span className={styles.sectionLabel}>
                        {journeyT('deliverables.all_user_deliverables', {
                          count: activeDeliverables.length,
                          defaultValue: `Todas las entregas (${activeDeliverables.length})`,
                        })}
                      </span>
                    </div>
                    <div className={styles.allDeliverablesGrid}>
                      {activeDeliverables.map((item, idx) => {
                        const isSelected = idx === safeDeliverableIndex;
                        const itemInsights = getDeliverableInsights(item);
                        const itemUrl = getDeliverableUrl(item);
                        const thumb = item.thumbnail_url || item.media_url;
                        const itemType = getDeliverableType(item);
                        return (
                          <div
                            key={item.id || idx}
                            className={`${styles.deliverableCardItem} ${isSelected ? styles.deliverableCardItemActive : ''}`}
                            onClick={() => setActiveDeliverableIndex(idx)}
                          >
                            <div className={styles.deliverableCardThumb}>
                              {thumb ? (
                                <img src={thumb} alt={itemType} />
                              ) : (
                                <div className={styles.deliverableCardFallback}>{idx + 1}</div>
                              )}
                              <span className={styles.deliverableCardTag}>{itemType}</span>
                            </div>
                            <div className={styles.deliverableCardInfo}>
                              <div className={styles.deliverableCardHeader}>
                                <strong>{`#${idx + 1} · ${itemType}`}</strong>
                                <small>{formatDate(item.created_at)}</small>
                              </div>
                              <div className={styles.deliverableCardStats}>
                                <span>👁️ {formatMetric(itemInsights.video_views ?? itemInsights.views)}</span>
                                <span>📈 {formatMetric(itemInsights.reach)}</span>
                                {(itemInsights.like_count || itemInsights.likes) ? (
                                  <span>❤️ {formatMetric(itemInsights.like_count ?? itemInsights.likes)}</span>
                                ) : null}
                              </div>
                              <div className={styles.deliverableCardActions}>
                                <button
                                  type="button"
                                  className={styles.deliverableCardSelectBtn}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveDeliverableIndex(idx);
                                  }}
                                >
                                  {isSelected
                                    ? journeyT('deliverables.selected_badge', { defaultValue: '✓ Seleccionada' })
                                    : journeyT('deliverables.view_detail', { defaultValue: 'Ver detalle' })}
                                </button>
                                {itemUrl && (
                                  <a
                                    href={itemUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.deliverableCardLinkBtn}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {journeyT('deliverables.open_post', { defaultValue: 'Abrir post' })} ↗
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className={styles.deliveryPaymentBox}><div><span>{journeyT('deliverables.compensation')}</span><strong>{formatMoney(compensation, offer.cash_currency || 'USD')}</strong></div><StatusPill tone={activeIsApproved ? 'success' : 'warning'}>{activeIsApproved ? journeyT('deliverables.payment.processing') : journeyT('deliverables.payment.pending')}</StatusPill></div>
                <div className={styles.deliveryProtection}><strong>{activeIsApproved ? journeyT('deliverables.protection.active_title') : journeyT('deliverables.protection.pending_title')}</strong>{activeIsApproved ? journeyT('deliverables.protection.active_body') : <>{journeyT('deliverables.protection.pending_body_before')} <b>{journeyT('payments.status.processing')}</b>. {journeyT('deliverables.protection.pending_body_after')}</>}</div>
                {activeIsApproved && <div className={styles.approvedMessage}><strong>{journeyT('deliverables.approved_title')}</strong>{journeyT('deliverables.approved_body')}</div>}
                {!activeIsApproved && activeProgress.requiredTotal !== null && !activeProgress.isComplete && <div className={styles.deliveryProtection}><strong>{journeyT('deliverables.incomplete_title')}</strong><span>{journeyT('deliverables.incomplete_body', { submitted: activeProgress.submittedTotal, required: activeProgress.requiredTotal })}</span></div>}
                <div className={styles.deliveryDetailActions}>
                  <Button variant="primary" onClick={() => approveOne(activeApplication)} loading={processing === activeApplication?.id} disabled={!activeDeliverable || activeIsApproved || !activeProgress.isComplete}>
                    {journeyT('deliverables.actions.approve')} <Check size={15} />
                  </Button>
                  <Button variant="outline" href={getDeliverableUrl(activeDeliverable) || undefined} target="_blank" disabled={!getDeliverableUrl(activeDeliverable)}>
                    {journeyT('deliverables.actions.view_post')} {activeDeliverables.length > 1 ? `(${getDeliverableType(activeDeliverable)})` : ''}
                  </Button>
                </div>
              </div>
            </aside>
          </div>
          <div className={styles.deliveryFooterNote}><span><strong>{journeyT('deliverables.important')}</strong> {journeyT('deliverables.important_body')}</span><span>{journeyT('deliverables.approved_summary', { completed: completed.length, total: accepted.length })}</span></div>
        </div>
      </section>
      <div className={styles.footerActions}><Button variant="ghost" onClick={goBack}>← {journeyT('common.back')}</Button><span className={styles.microcopy}>{allApproved ? journeyT('deliverables.footer.all_approved') : journeyT('deliverables.footer.pending', { count: ready.length })}</span><Button variant="primary" className={styles.nextButton} onClick={goNext} disabled={!allApproved}>{journeyT('deliverables.actions.continue')} <ArrowRight size={17} /></Button></div>
    </>
  );
};

const PaymentsStep = ({ offer, payment, applications, goBack, goNext, steps, onPaymentChange }) => {
  const allocations = Array.isArray(payment?.allocations) ? payment.allocations : [];
  const creatorByUsername = useMemo(() => new Map((applications || []).map((application) => [application.creator_username, application])), [applications]);
  const processingAllocations = allocations.filter((allocation) => PAYMENT_PROTECTED_STATES.has(allocation.transfer_status));
  const availableAllocations = allocations.filter((allocation) => allocation.transfer_status === 'transferred');
  const protectedAmount = allocations
    .filter((allocation) => PAYMENT_PROTECTED_STATES.has(allocation.transfer_status))
    .reduce((total, allocation) => total + Number(allocation.creator_amount || 0), 0);
  const paymentCurrency = payment?.currency || offer.cash_currency || 'USD';
  const paymentStep = steps.find((step) => step.id === 'payments');
  const allRows = allocations.length > 0 ? allocations : [];
  const paymentNeedsAttention = !payment || !TERMINAL_PAYMENT_STATES.has(payment.payment_status);

  return (
    <>
      <StepHeader eyebrow={`${paymentStep?.number} · ${journeyT('steps.payments')}`} title={journeyT('payments.title')} description={journeyT('payments.description')} number={paymentStep?.number} />
      {isPaidOffer(offer) ? (
        <>
          <section className={styles.paymentFlowFrame}>
            <div className={styles.processPath}>
              <div className={styles.processStep}><strong>1 · {journeyT('payments.process.approved_title')}</strong><span>{journeyT('payments.process.approved_body')}</span></div>
              <div className={styles.processStep}><strong>2 · {journeyT('payments.process.processing_title')}</strong><span>{journeyT('payments.process.processing_body')}</span></div>
              <div className={styles.processStep}><strong>3 · {journeyT('payments.process.available_title')}</strong><span>{journeyT('payments.process.available_body')}</span></div>
            </div>
            <div className={styles.paymentSummaryGrid}>
              <div className={styles.paySummary}><span>{journeyT('payments.summary.processing')}</span><strong>{processingAllocations.length}</strong><small>{journeyT('payments.summary.processing_help')}</small></div>
              <div className={styles.paySummary}><span>{journeyT('payments.summary.available')}</span><strong>{availableAllocations.length}</strong><small>{journeyT('payments.summary.available_help')}</small></div>
              <div className={styles.paySummary}><span>{journeyT('payments.summary.protected_amount')}</span><strong>{formatMoney(protectedAmount, paymentCurrency)}</strong><small>{journeyT('payments.summary.protected_help')}</small></div>
            </div>
            <div className={styles.paymentLedger}>
              <div className={styles.ledgerHead}><div>{journeyT('creators.table.creator')}</div><div>{journeyT('payments.ledger.payment')}</div><div>{journeyT('payments.ledger.refund_window')}</div><div>{journeyT('payments.ledger.availability')}</div></div>
              <div className={styles.paymentLedgerBody}>
                {allRows.length ? allRows.map((allocation) => {
                  const application = creatorByUsername.get(allocation.creator_username);
                  const creatorName = application ? getApplicationCreatorName(application) : allocation.creator_username || 'Creador';
                  const state = getPaymentAllocationState(allocation);
                  return (
                    <div className={styles.ledgerRow} key={allocation.id || allocation.creator_username}>
                      <div className={styles.ledgerCreator}><Avatar size="md" src={application?.creator_profile_image} name={creatorName} alt="" /><div><h4>{creatorName}</h4><p>@{allocation.creator_username || 'creator'}</p></div></div>
                      <div><span className={`${styles.paymentBadge} ${styles[`paymentBadge${state.tone.charAt(0).toUpperCase()}${state.tone.slice(1)}`]}`}>{state.label}</span><div className={styles.ledgerDate}>{formatMoney(allocation.creator_amount, allocation.currency || paymentCurrency)}</div></div>
                      <div className={styles.refundWindow}><strong>{getRefundWindowLabel(allocation)}</strong><small>{journeyT('payments.individual_protection')}</small></div>
                      <div className={styles.ledgerDate}><strong>{getAvailabilityLabel(allocation)}</strong><span>{getAllocationTimelineLabel(allocation)}</span></div>
                    </div>
                  );
                }) : <div className={styles.paymentLedgerEmpty}><Clock3 size={18} /> {payment ? journeyT('payments.empty') : journeyT('payments.syncing')}</div>}
              </div>
            </div>
            <div className={styles.paymentFlowNote}><div className={styles.noteIcon}>↻</div><div><strong>{journeyT('payments.note.title')}</strong><p>{journeyT('payments.note.body')}</p></div></div>
          </section>
          <details className={styles.paymentDetails} open={paymentNeedsAttention}>
            <summary><div className={styles.paymentStatusPanelHeader}><div><span className={styles.sectionLabel}>{journeyT('payments.details.label')}</span><h2>{journeyT('payments.details.title')}</h2></div><span className={styles.paymentStatusCode}>{payment ? journeyT(`paymentStatus.${PAYMENT_STATUS_KEYS[payment.payment_status] || 'updated'}`) : journeyT('payments.syncing')}</span></div></summary>
            <div className={styles.paymentDetailsBody}><PaidCollabPaymentStatus collabId={offer.id} role="business" collab={offer} onPaymentChange={onPaymentChange} /></div>
          </details>
        </>
      ) : <section className={styles.panel}><div className={styles.panelTitle}><ShieldCheck size={18} /><div><h2>{journeyT('payments.non_monetary.title')}</h2><span>{journeyT('payments.non_monetary.subtitle')}</span></div></div><p className={styles.panelCopy}>{journeyT('payments.non_monetary.body')}</p></section>}
      <div className={styles.footerActions}><Button variant="ghost" onClick={goBack}>← {journeyT('payments.actions.back')}</Button><span className={styles.microcopy}>{offer.lifecycle_status === 'completed' ? journeyT('payments.footer.closed') : journeyT('payments.footer.complete_deliverables')}</span><Button variant="primary" className={styles.nextButton} onClick={goNext} disabled={offer.lifecycle_status !== 'completed'}>{offer.lifecycle_status === 'completed' ? <>{journeyT('payments.actions.close')} <ArrowRight size={17} /></> : journeyT('payments.footer.waiting')}</Button></div>
    </>
  );
};

const CompletedStep = ({ offer, applications, payment, onReport, onDownloadReport, onDuplicate, onExit, downloadingReport, duplicating, steps }) => {
  const completedApplications = (applications || []).filter((application) => ['accepted', 'completed'].includes(application.status));
  const completedCreators = completedApplications.filter((application) => application.status === 'completed');
  const deliverables = completedApplications.flatMap(getApplicationDeliverables);
  const allocations = Array.isArray(payment?.allocations) ? payment.allocations : [];
  const allocationByUsername = new Map(allocations.map((allocation) => [allocation.creator_username, allocation]));
  const creatorTarget = Math.max(Number(offer.quantity || 0), completedApplications.length);
  const approvedDeliverables = completedApplications.reduce((total, application) => (
    application.status === 'completed' ? total + getApplicationDeliverables(application).length : total
  ), 0);
  const processingPayments = isPaidOffer(offer)
    ? allocations.filter((allocation) => PAYMENT_PROTECTED_STATES.has(allocation.transfer_status)).length
    : null;

  const sumMetric = (keys) => {
    const values = deliverables
      .map(getDeliverableInsights)
      .map((insights) => keys.map((key) => insights[key]).find((value) => value !== null && value !== undefined && value !== ''))
      .filter((value) => value !== undefined && value !== null && value !== '' && Number.isFinite(Number(value)));
    return values.length ? values.reduce((total, value) => total + Number(value), 0) : null;
  };

  const metrics = [
    [journeyT('completed.metrics.views'), sumMetric(['video_views', 'views'])],
    [journeyT('completed.metrics.reach'), sumMetric(['reach'])],
    [journeyT('completed.metrics.likes'), sumMetric(['likes', 'like_count'])],
    [journeyT('completed.metrics.comments'), sumMetric(['comments', 'comments_count'])],
    [journeyT('completed.metrics.saves'), sumMetric(['saves', 'saved'])],
  ];
  const paymentDescription = isPaidOffer(offer) ? journeyT('completed.payment_paid') : journeyT('completed.payment_exchange');
  const completionDescription = journeyT('completed.description', { completed: completedCreators.length, total: creatorTarget || completedCreators.length, payment: paymentDescription });
  const completedStep = steps.find((step) => step.id === 'completed');

  return (
    <>
      <StepHeader eyebrow={`${completedStep?.number} · ${journeyT('completed.eyebrow')}`} title={journeyT('completed.title')} description={journeyT('completed.header_description')} number={completedStep?.number} />

      <section className={styles.completionFrame}>
        <div className={styles.completionMasthead}>
          <div className={styles.completionMastheadCopy}>
            <span className={styles.completionEyebrow}><CheckCircle2 size={14} /> {journeyT('completed.badge')}</span>
            <h2>{offer.title}</h2>
            <p>{completionDescription}</p>
          </div>
          <div className={styles.completionStamp} aria-label={journeyT('completed.stamp_aria', { completed: completedCreators.length, total: creatorTarget || completedCreators.length })}>
            <strong>{completedCreators.length}/{creatorTarget || completedCreators.length}</strong>
            <span>{journeyT('completed.creators_completed')}</span>
          </div>
        </div>

        <div className={styles.completionStats} aria-label={journeyT('completed.stats_aria')}>
          <div className={styles.completionMetric}><span>{journeyT('completed.stats.creators')}</span><strong>{completedCreators.length} / {creatorTarget || completedCreators.length}</strong></div>
          <div className={styles.completionMetric}><span>{journeyT('completed.stats.deliverables')}</span><strong>{approvedDeliverables}</strong></div>
          {isPaidOffer(offer) && <div className={styles.completionMetric}><span>{journeyT('completed.stats.payments')}</span><strong>{processingPayments}</strong></div>}
        </div>

        <button className={styles.reportGateway} type="button" onClick={onReport} aria-label={journeyT('completed.report.open_aria')}>
          <span className={styles.reportGatewayMain}>
            <span className={styles.reportKicker}>{journeyT('completed.report.kicker')}</span>
            <strong>{journeyT('completed.report.title')}</strong>
            <span className={styles.reportGatewayDescription}>{journeyT('completed.report.description')}</span>
            <span className={styles.reportGatewayCta}>{journeyT('completed.report.open')} <ArrowRight size={16} /></span>
          </span>
          <span className={styles.reportGatewayVisual} aria-hidden="true"><strong>EMV</strong><span>{journeyT('completed.report.results')}</span></span>
        </button>

        <div className={styles.completionUtility}>
          <Button variant="outline" onClick={onDownloadReport} loading={downloadingReport} disabled={duplicating}><Download size={15} /> {journeyT('completed.actions.download')}</Button>
          <Button variant="ghost" onClick={onDuplicate} loading={duplicating} disabled={downloadingReport}><Copy size={15} /> {journeyT('completed.actions.duplicate')}</Button>
        </div>

        <section className={styles.reportPreview} aria-label={journeyT('completed.results.aria')}>
          <div className={styles.resultsHeader}>
            <div><span className={styles.sectionLabel}>{journeyT('completed.results.label')}</span><h3>{journeyT('completed.results.title')}</h3></div>
            <p>{journeyT('completed.results.description')}</p>
          </div>
          <div className={styles.impactRow}>
            {metrics.map(([label, value]) => <div className={styles.impactCell} key={label}><span>{label}</span><strong>{formatMetric(value)}</strong></div>)}
          </div>
          <div className={styles.completionCreators}>
            {completedApplications.map((application) => {
              const username = getApplicationUsername(application);
              const allocation = allocationByUsername.get(username);
              const paymentState = allocation ? getPaymentAllocationState(allocation) : { label: isPaidOffer(offer) ? journeyT('deliverables.payment.pending') : journeyT('completed.no_payments'), tone: 'neutral' };
              const deliverableCount = getApplicationDeliverables(application).length;
              return (
                <div className={`${styles.completionCreatorCard} ${!isPaidOffer(offer) ? styles.completionCreatorCardNoPayment : ''}`} key={application.id || username}>
                  <Avatar size="md" src={application.creator_profile_image} name={getApplicationCreatorName(application)} alt="" />
                  <div className={styles.completionCreatorIdentity}><h4>@{username || 'creator'}</h4><p>{journeyT(deliverableCount === 1 ? 'deliverables.count_one' : 'deliverables.count_other', { count: deliverableCount })} · {isPaidOffer(offer) && allocation ? formatMoney(allocation.creator_amount, allocation.currency || offer.cash_currency || 'USD') : journeyT('completed.execution_closed')}</p></div>
                  {isPaidOffer(offer) && <span className={`${styles.completionCreatorStatus} ${styles[`completionCreatorStatus${paymentState.tone.charAt(0).toUpperCase()}${paymentState.tone.slice(1)}`]}`}>{paymentState.label}</span>}
                </div>
              );
            })}
            {!completedApplications.length && <div className={styles.completionEmpty}>{journeyT('completed.empty')}</div>}
          </div>
        </section>
      </section>

      <div className={styles.footerActions}><span className={styles.microcopy}>{journeyT('completed.footer')}</span><Button variant="primary" onClick={onExit}>{journeyT('header.back')} <ArrowRight size={17} /></Button></div>
    </>
  );
};

const BusinessCollabJourney = () => {
  const { i18n: localeI18n } = useTranslation('collab');
  const { subscriptionInfo, refreshSubscriptionInfo } = useAuth();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [applications, setApplications] = useState([]);
  const applicationsRef = useRef([]);
  const [invitations, setInvitations] = useState([]);
  const [directInvitations, setDirectInvitations] = useState([]);
  const [payment, setPayment] = useState(null);
  const [activeStep, setActiveStep] = useState('review');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [sendingInvites, setSendingInvites] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  const handlePaymentChange = useCallback((nextPayment) => {
    setPayment(nextPayment);
  }, []);

  useEffect(() => {
    applicationsRef.current = applications;
  }, [applications]);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const requests = [getCollabSummary(id), getCollabApplications(id), getRecipientInvitations(id), getDirectInvitations(id)];
      const [offerResponse, applicationsResponse, invitationsResponse, directInvitationsResponse] = await Promise.all(requests);
      const nextOffer = offerResponse.data;
      setOffer(nextOffer);
      const appData = applicationsResponse.data;
      const nextApplications = Array.isArray(appData) ? appData : appData.results || [];
      applicationsRef.current = nextApplications;
      setApplications(nextApplications);
      setInvitations(Array.isArray(invitationsResponse.data) ? invitationsResponse.data : []);
      setDirectInvitations(Array.isArray(directInvitationsResponse.data) ? directInvitationsResponse.data : []);
      if (isPaidOffer(nextOffer)) {
        try { const paymentResponse = await getCollabPaymentStatus(id); setPayment(paymentResponse.data); } catch (error) { setPayment(null); }
      } else setPayment(null);
    } catch (error) {
      showToast({
        tone: 'error',
        title: journeyT('toasts.error_title'),
        description: readError(error, journeyT('errors.load')),
        action: { label: journeyT('toasts.retry'), onClick: () => load() },
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    if (params.get('stripe') !== 'success' || !sessionId) return undefined;

    let cancelled = false;
    const confirmCheckout = async () => {
      try {
        await confirmStripeCheckoutSession(sessionId);
        if (cancelled) return;
        await refreshSubscriptionInfo();
        params.delete('stripe');
        params.delete('session_id');
        const search = params.toString();
        navigate(`${location.pathname}${search ? `?${search}` : ''}`, { replace: true });
        await load({ silent: true });
        showToast({
          tone: 'success',
          title: journeyT('toasts.next_step_title'),
          description: journeyT('completed.payment_paid'),
        });
      } catch (error) {
        if (!cancelled) {
          showToast({
            tone: 'error',
            title: journeyT('toasts.error_title'),
            description: readError(error, journeyT('errors.load')),
          });
        }
      }
    };

    confirmCheckout();
    return () => { cancelled = true; };
  }, [load, location.pathname, location.search, navigate, refreshSubscriptionInfo]);

  const paid = isPaidOffer(offer);
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const steps = useMemo(() => stepDefinitions(paid, localeI18n.resolvedLanguage || localeI18n.language), [paid, localeI18n.language, localeI18n.resolvedLanguage]);

  useEffect(() => {
    if (!paid) {
      setSubscriptionChecked(true);
      return undefined;
    }
    let cancelled = false;
    setSubscriptionChecked(false);
    refreshSubscriptionInfo().finally(() => {
      if (!cancelled) setSubscriptionChecked(true);
    });
    return () => { cancelled = true; };
  }, [paid, refreshSubscriptionInfo]);
  const deepLink = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const requestedStep = params.get('step');
    const legacyPerformanceTab = ['performance', 'metrics'].includes(params.get('tab'));
    const targetStep = legacyPerformanceTab
      ? 'deliverables'
      : JOURNEY_STEPS.has(requestedStep) ? requestedStep : null;
    return {
      targetStep,
      creatorUsername: params.get('creator') || params.get('creatorUsername') || '',
    };
  }, [location.search]);
  const completedSteps = useMemo(() => {
    const result = new Set();
    if (!offer) return result;
    result.add('review');
    if (paid && TERMINAL_PAYMENT_STATES.has(payment?.payment_status || offer.paid_collab_payment_status)) result.add('funding');
    if (offer.status === 'active' && offer.is_feed_visible !== false) result.add('publish');
    if (getCoveredCreatorCount(offer, applications) >= Number(offer.quantity || 0) && Number(offer.quantity || 0) > 0) result.add('creators');
    const accepted = applications.filter((application) => ['accepted', 'completed'].includes(application.status));
    if (accepted.length > 0 && accepted.every((application) => application.status === 'completed')) result.add('deliverables');
    if (offer.lifecycle_status === 'completed') {
      if (paid) result.add('payments');
      result.add('completed');
    }
    return result;
  }, [applications, offer, paid, payment]);
  const availableSteps = useMemo(() => {
    const result = new Set();
    if (offer?.status === 'active' && offer.is_feed_visible !== false) result.add('creators');
    if (applications.some((application) => ['accepted', 'completed'].includes(application.status) && getApplicationDeliverables(application).length > 0)) result.add('deliverables');
    if (paid && applications.some((application) => application.status === 'completed')) result.add('payments');
    return result;
  }, [applications, offer, paid]);

  const canOpenStep = useCallback((stepId) => {
    const stepIndex = steps.findIndex((step) => step.id === stepId);
    if (stepIndex < 0) return false;
    const isPublished = offer?.status === 'active' && offer.is_feed_visible !== false;
    const creatorsIndex = steps.findIndex((step) => step.id === 'creators');
    if (!paid && !isPublished && stepIndex >= creatorsIndex) return false;
    if (completedSteps.has(stepId) || availableSteps.has(stepId)) return true;
    return steps.slice(0, stepIndex).every((step) => completedSteps.has(step.id));
  }, [availableSteps, completedSteps, offer, paid, steps]);

  useEffect(() => {
    if (!offer) return;
    if (deepLink.targetStep && canOpenStep(deepLink.targetStep)) {
      setActiveStep((current) => current === 'review' ? deepLink.targetStep : current);
      return;
    }
    const paymentStatus = payment?.payment_status || offer.paid_collab_payment_status;
    const accepted = applications.filter((application) => ['accepted', 'completed'].includes(application.status));
    const covered = getCoveredCreatorCount(offer, applications);
    const allDeliverablesApproved = accepted.length > 0 && accepted.every((application) => application.status === 'completed');
    const isPublished = offer.status === 'active' && offer.is_feed_visible !== false;
    const next = offer.lifecycle_status === 'completed' ? 'completed'
      : paid && !TERMINAL_PAYMENT_STATES.has(paymentStatus) ? 'funding'
        : !isPublished ? paid ? 'publish' : 'review'
          : covered < Number(offer.quantity || 0) ? 'creators'
            : !allDeliverablesApproved ? 'deliverables' : paid ? 'payments' : 'completed';
    setActiveStep((current) => current === 'review' ? next : current);
  }, [applications, canOpenStep, deepLink.targetStep, offer, paid, payment]);

  useEffect(() => {
    if (activeStep !== 'payments' || !paid) return undefined;
    let cancelled = false;
    getCollabPaymentStatus(id)
      .then((response) => {
        if (!cancelled) setPayment(response.data);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [activeStep, id, paid]);

  const refresh = () => { setRefreshing(true); load({ silent: true }); };
  const handleApprove = async (application) => {
    setProcessing(application.id);
    try {
      const response = await api.post(`/feed/applications/${application.id}/approve/`);
      setApplications((current) => current.map((item) => item.id === application.id ? response.data : item));
      showToast({
        tone: 'success',
        title: journeyT('toasts.creator_approved_title'),
        description: journeyT('toasts.creator_approved'),
      });
    } catch (error) {
      showToast({ tone: 'error', title: journeyT('toasts.error_title'), description: readError(error, journeyT('errors.approve_creator')) });
    } finally { setProcessing(null); }
  };
  const handleReject = async (application) => {
    setProcessing(application.id);
    try {
      const response = await api.post(`/feed/applications/${application.id}/reject/`);
      setApplications((current) => current.map((item) => item.id === application.id ? response.data : item));
      showToast({ tone: 'success', title: journeyT('toasts.application_rejected_title'), description: journeyT('toasts.application_rejected') });
    } catch (error) {
      showToast({ tone: 'error', title: journeyT('toasts.error_title'), description: readError(error, journeyT('errors.reject_application')) });
    } finally { setProcessing(null); }
  };
  const handleComplete = async (application) => {
    setProcessing(application.id);
    try {
      const response = await completeCollabApplication(application.id);
      // The complete endpoint wraps the serialized application in an
      // `application` property. Keep the journey state normalized so the
      // deliverables screen can immediately render the approved row instead
      // of losing its id/status and collapsing into an empty state.
      const completedApplication = response.data?.application || response.data;
      if (!completedApplication?.id) throw new Error('La aplicación aprobada no devolvió un identificador válido.');
      const currentApplications = applicationsRef.current.length ? applicationsRef.current : applications;
      const applicationsAfterApproval = currentApplications.map((item) => (
        item.id === application.id ? completedApplication : item
      ));
      applicationsRef.current = applicationsAfterApproval;
      setApplications(applicationsAfterApproval);
      let refreshedOffer = null;
      try {
        const summaryResponse = await getCollabSummary(id);
        refreshedOffer = summaryResponse.data;
        setOffer(summaryResponse.data);
        if (isPaidOffer(summaryResponse.data)) {
          const paymentResponse = await getCollabPaymentStatus(id);
          setPayment(paymentResponse.data);
        }
      } catch {
        // The approval is already persisted; the next Journey refresh will reconcile these summaries.
      }

      const nextOffer = refreshedOffer || offer;
      const acceptedAfterApproval = applicationsAfterApproval.filter((item) => ['accepted', 'completed'].includes(item.status));
      const allDeliverablesApproved = acceptedAfterApproval.length > 0
        && acceptedAfterApproval.every((item) => item.status === 'completed');
      const nextStep = allDeliverablesApproved
        ? isPaidOffer(nextOffer) ? 'payments' : 'completed'
        : 'deliverables';
      const nextApplication = acceptedAfterApproval.find((item) => (
        item.status === 'accepted' && getDeliverableProgress(nextOffer, item).isComplete
      ));
      const nextCreator = nextStep === 'deliverables' && nextApplication
        ? `&creator=${encodeURIComponent(getApplicationUsername(nextApplication))}`
        : '';

      // Move the business user to the next actionable surface immediately.
      // Keeping the stale creator deep-link after the mutation leaves the
      // journey without a valid selected application and can render blank.
      navigate(`/business/collabs/${id}/journey?step=${nextStep}${nextCreator}`, { replace: true });
      setActiveStep(nextStep);
      showToast({
        tone: 'success',
        title: journeyT('toasts.delivery_approved_title'),
        description: journeyT('toasts.delivery_approved'),
      });
      return true;
    } catch (error) {
      showToast({ tone: 'error', title: journeyT('toasts.error_title'), description: readError(error, journeyT('errors.approve_delivery')) });
      return false;
    } finally { setProcessing(null); }
  };
  const handlePublish = async () => {
    setPublishing(true);
    try {
      const response = await publishCollabOffer(id);
      setOffer((current) => ({ ...current, ...(response.data.offer || {}), status: response.data.offer?.status || 'active' }));
      showToast({
        tone: 'success',
        title: journeyT('toasts.collab_published_title'),
        description: journeyT('toasts.collab_published'),
        action: { label: journeyT('toasts.manage_creators'), to: `/business/collabs/${id}/journey?step=creators` },
      });
      setActiveStep(isPaidOffer(offer) ? 'publish' : 'creators');
    } catch (error) {
      if (error?.response?.status === 402) navigate(`/business/payment/required?offerId=${id}`);
      else showToast({ tone: 'error', title: journeyT('toasts.error_title'), description: readError(error, journeyT('errors.publish')) });
    } finally { setPublishing(false); }
  };
  const handleSendInvites = async (rawEmails) => {
    const emails = [...new Set(rawEmails.split(/[\n,]/).map((email) => email.trim().toLowerCase()).filter(Boolean))];
    const invalidEmails = emails.filter((email) => !/^\S+@\S+\.\S+$/.test(email));
    if (invalidEmails.length) { showToast({ tone: 'error', title: journeyT('toasts.error_title'), description: journeyT('errors.invalid_emails') }); return false; }
    if (emails.length > 50) { showToast({ tone: 'error', title: journeyT('toasts.error_title'), description: journeyT('errors.email_limit') }); return false; }
    if (!emails.length) return false;
    setSendingInvites(true);
    try {
      const response = await sendRecipientInvites(id, emails);
      const incoming = response.data?.invitations || [];
      setInvitations((current) => {
        const incomingIds = new Set(incoming.map((invite) => invite.id));
        return [...incoming, ...current.filter((invite) => !incomingIds.has(invite.id))];
      });
      showToast({
        tone: response.data?.failed?.length ? 'warning' : 'success',
        title: journeyT('toasts.invites_title'),
        description: response.data?.failed?.length ? journeyT('toasts.invites_partial') : journeyT('toasts.invites_sent'),
        action: { label: journeyT('toasts.view_creators'), to: `/business/collabs/${id}/journey?step=creators` },
      });
      return true;
    } catch (error) {
      showToast({ tone: 'error', title: journeyT('toasts.error_title'), description: readError(error, journeyT('errors.send_invites')) });
      return false;
    } finally { setSendingInvites(false); }
  };

  const handleDownloadReport = async () => {
    if (downloadingReport) return;
    setDownloadingReport(true);
    try {
      const response = await downloadCollabReport(id, 'pdf');
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `creator-emv-report-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      showToast({ tone: 'success', title: journeyT('toasts.report_downloaded_title'), description: journeyT('toasts.report_downloaded') });
    } catch (error) {
      showToast({ tone: 'error', title: journeyT('toasts.error_title'), description: readError(error, journeyT('errors.download_report')) });
    } finally { setDownloadingReport(false); }
  };

  const handleDuplicate = async () => {
    if (duplicating) return;
    setDuplicating(true);
    try {
      const response = await duplicateCollabOffer(id);
      const duplicatedId = response.data?.id;
      showToast({ tone: 'success', title: journeyT('toasts.duplicated_title'), description: journeyT('toasts.duplicated') });
      navigate(duplicatedId ? `/business/collabs/${duplicatedId}/edit` : '/business/collabs');
    } catch (error) {
      showToast({ tone: 'error', title: journeyT('toasts.error_title'), description: readError(error, journeyT('errors.duplicate')) });
    } finally { setDuplicating(false); }
  };

  if (loading && !offer) return <div className={styles.loadingPage}><RefreshCw className={styles.spin} size={22} /><span>{journeyT('loading')}</span></div>;
  if (!offer) return null;

  const currentStepIndex = Math.max(0, steps.findIndex((step) => step.id === activeStep));
  const goNext = () => setActiveStep(steps[Math.min(currentStepIndex + 1, steps.length - 1)].id);
  const goBack = () => setActiveStep(steps[Math.max(currentStepIndex - 1, 0)].id);
  const handleCandidates = () => {
    if (offer.status === 'active' && offer.is_feed_visible !== false) setActiveStep('creators');
    else showToast({ tone: 'info', title: journeyT('toasts.next_step_title'), description: journeyT('errors.publish_before_creators') });
  };
  const handleEdit = () => {
    const isPaidAndFunded = isPaidOffer(offer) && ['paid', 'partially_refunded', 'fully_refunded', 'disputed'].includes(offer.paid_collab_payment_status);
    if (offer.can_edit === false || isPaidAndFunded) {
      showToast({
        tone: 'info',
        title: journeyT('toasts.edit_locked_title', { defaultValue: 'Edición protegida' }),
        description: isPaidAndFunded
          ? journeyT('review.edit_locked_paid', { defaultValue: 'Las colaboraciones pagadas no pueden editarse una vez financiadas.' })
          : journeyT('review.edit_locked', { defaultValue: 'Esta colaboración no se puede editar porque ya cuenta con entregas de creadores o está en curso.' }),
      });
      return;
    }
    navigate(`/business/collabs/${id}/edit`);
  };
  const handleFindCreators = () => navigate(`/business/creators/discovery?offerId=${id}`);
  const handleReviewDeliverable = (application) => {
    const username = getApplicationUsername(application);
    const target = username ? `&creator=${encodeURIComponent(username)}` : '';
    navigate(`/business/collabs/${id}/journey?step=deliverables${target}`);
    setActiveStep('deliverables');
  };
  const selectStep = (stepId) => {
    if (stepId === activeStep || completedSteps.has(stepId) || availableSteps.has(stepId)) {
      setActiveStep(stepId);
      navigate(`/business/collabs/${id}/journey?step=${stepId}`);
    }
  };
  const renderStep = () => {
    if (activeStep === 'review') return <ReviewStep offer={offer} goNext={goNext} onPublish={handlePublish} publishing={publishing} onEdit={handleEdit} onCandidates={handleCandidates} steps={steps} />;
    if (activeStep === 'funding') return <FundingStep offer={offer} payment={payment} goNext={goNext} goBack={goBack} steps={steps} onPaymentChange={handlePaymentChange} subscriptionInfo={subscriptionInfo} subscriptionChecked={subscriptionChecked} onActivateSubscription={() => navigate(`/business/payment/required?offerId=${id}`)} />;
    if (activeStep === 'publish') return <PublishStep offer={offer} payment={payment} onPublish={handlePublish} publishing={publishing} goNext={goNext} steps={steps} />;
    if (activeStep === 'creators') return <CreatorsStep offer={offer} applications={applications} invitations={invitations} directInvitations={directInvitations} onApprove={handleApprove} onReject={handleReject} onSendInvites={handleSendInvites} onFindCreators={handleFindCreators} onReviewDeliverable={handleReviewDeliverable} processing={processing} sendingInvites={sendingInvites} goBack={goBack} goNext={goNext} steps={steps} />;
    if (activeStep === 'deliverables') return <DeliverablesStep offer={offer} applications={applications} initialApplicationUsername={deepLink.creatorUsername} onComplete={handleComplete} processing={processing} goBack={goBack} goNext={goNext} steps={steps} />;
    if (activeStep === 'payments') return <PaymentsStep offer={offer} payment={payment} applications={applications} goBack={goBack} goNext={goNext} steps={steps} onPaymentChange={handlePaymentChange} />;
    return <CompletedStep offer={offer} applications={applications} payment={payment} onReport={() => navigate(`/business/collabs/${id}/report`)} onDownloadReport={handleDownloadReport} onDuplicate={handleDuplicate} onExit={() => navigate('/business/collabs')} downloadingReport={downloadingReport} duplicating={duplicating} steps={steps} />;
  };

  return <div className={styles.journey} data-step={currentStepIndex + 1} data-active-step={activeStep}>
    <JourneyHeader offer={offer} currentStep={activeStep} steps={steps} onExit={() => navigate('/business/collabs')} onRefresh={refresh} refreshing={refreshing} />
    <div className={styles.workspace}><JourneyRail steps={steps} activeStep={activeStep} completedSteps={completedSteps} availableSteps={availableSteps} onSelect={selectStep} /><main className={styles.stage}><div className={styles.scene}>{renderStep()}</div></main></div>
  </div>;
};

export default BusinessCollabJourney;
